// Slice 45 — Subprocessors tab (Art. 28 register). List + add + soft-remove.

import { useEffect, useState } from "react";
import { type Subprocessor, SubprocessorsApi } from "../../api/client";
import { useShell } from "../../shell/HqShellContext";
import { HqBadge, HqButton, HqCard, HqInput, HqModal, Icons } from "../../ui";
import { fmtDateMs } from "./format";

export interface SubprocessorsTabProps {
  rows: Subprocessor[];
  onMutated: () => void;
}

export function SubprocessorsTab({ rows, onMutated }: SubprocessorsTabProps) {
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <HqCard
      title="Subprocessor register"
      icon={Icons.Shield}
      right={
        <HqButton
          size="sm"
          variant="primary"
          leadingIcon={Icons.Plus}
          onClick={() => setCreateOpen(true)}
        >
          Add subprocessor
        </HqButton>
      }
      padding={false}
    >
      <p
        style={{
          margin: 0,
          padding: "12px 16px",
          fontSize: 11.5,
          color: "var(--hq-text-muted)",
          lineHeight: 1.55,
          borderBottom: "1px solid var(--hq-border)",
        }}
      >
        GDPR Art. 28 register. Removed entries are soft-deleted — the history survives so future
        audits can reconstruct who processed data when.
      </p>
      {rows.length === 0 ? (
        <div
          style={{ padding: 24, textAlign: "center", color: "var(--hq-text-dim)", fontSize: 12.5 }}
        >
          No subprocessors registered yet.
        </div>
      ) : (
        <div style={{ maxHeight: 540, overflow: "auto" }} className="hq-scroll">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr
                style={{
                  position: "sticky",
                  top: 0,
                  background: "var(--hq-bg-raised)",
                  borderBottom: "1px solid var(--hq-border)",
                  textAlign: "left",
                  zIndex: 1,
                }}
              >
                <Th>Vendor</Th>
                <Th>Purpose</Th>
                <Th>Location</Th>
                <Th>Transfer</Th>
                <Th>Added</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Row key={r.id} row={r} onRemoved={onMutated} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          onMutated();
        }}
      />
    </HqCard>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <td
      style={{
        padding: "10px 14px",
        color: "var(--hq-text-dim)",
        fontFamily: "var(--hq-font-mono)",
        fontSize: 10.5,
        letterSpacing: ".1em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </td>
  );
}

function Cell({
  children,
  right,
  mono,
}: {
  children?: React.ReactNode;
  right?: boolean;
  mono?: boolean;
}) {
  return (
    <td
      style={{
        padding: "12px 14px",
        textAlign: right ? "right" : "left",
        color: "var(--hq-text)",
        fontFamily: mono ? "var(--hq-font-mono)" : "var(--hq-font-sans)",
        fontSize: mono ? 11 : 12,
        verticalAlign: "middle",
      }}
    >
      {children}
    </td>
  );
}

function Row({ row, onRemoved }: { row: Subprocessor; onRemoved: () => void }) {
  const { toast } = useShell();
  const removed = row.removedAt != null;
  const eeaIsh = /EEA|GDPR/i.test(row.transferMechanism);

  async function remove() {
    const res = await SubprocessorsApi.remove(row.id);
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    toast("Subprocessor removed (soft-delete).", "warn");
    onRemoved();
  }

  return (
    <tr style={{ borderBottom: "1px solid var(--hq-border)", opacity: removed ? 0.55 : 1 }}>
      <Cell>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 500 }}>{row.name}</span>
          {row.dpaUrl && (
            <a
              href={row.dpaUrl}
              target="_blank"
              rel="noreferrer noopener"
              style={{
                fontSize: 10.5,
                color: "var(--hq-accent)",
                fontFamily: "var(--hq-font-mono)",
                textDecoration: "none",
              }}
            >
              DPA →
            </a>
          )}
        </div>
      </Cell>
      <Cell>{row.purpose}</Cell>
      <Cell mono>{row.location}</Cell>
      <Cell>
        <HqBadge tone={eeaIsh ? "success" : "warn"} size="sm">
          {row.transferMechanism}
        </HqBadge>
      </Cell>
      <Cell mono>{fmtDateMs(row.addedAt)}</Cell>
      <Cell right>
        {removed ? (
          <HqBadge tone="warn" size="sm">
            removed {fmtDateMs(row.removedAt)}
          </HqBadge>
        ) : (
          <HqButton variant="ghost" size="sm" leadingIcon={Icons.X} onClick={() => void remove()}>
            Remove
          </HqButton>
        )}
      </Cell>
    </tr>
  );
}

function CreateModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [location, setLocation] = useState("");
  const [dpaUrl, setDpaUrl] = useState("");
  const [transferMechanism, setTransferMechanism] = useState("EEA · GDPR");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setName("");
      setPurpose("");
      setLocation("");
      setDpaUrl("");
      setTransferMechanism("EEA · GDPR");
      setBusy(false);
      setErr(null);
    }
  }, [open]);

  async function submit() {
    if (!name.trim() || !purpose.trim() || !location.trim()) {
      setErr("Name, purpose and location are required.");
      return;
    }
    setBusy(true);
    setErr(null);
    const res = await SubprocessorsApi.create({
      name: name.trim(),
      purpose: purpose.trim(),
      location: location.trim(),
      ...(dpaUrl.trim() ? { dpaUrl: dpaUrl.trim() } : {}),
      transferMechanism: transferMechanism.trim() || "EEA · GDPR",
    });
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message);
      return;
    }
    onCreated();
  }

  return (
    <HqModal
      open={open}
      onClose={onClose}
      title="Add subprocessor"
      width={520}
      footer={
        <>
          <HqButton variant="ghost" onClick={onClose}>
            Cancel
          </HqButton>
          <HqButton
            variant="primary"
            leadingIcon={Icons.Check}
            loading={busy}
            onClick={() => void submit()}
          >
            Add
          </HqButton>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="Name">
          <HqInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Dokploy"
            autoFocus
          />
        </Field>
        <Field label="Purpose">
          <HqInput
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="Application hosting / container orchestration"
          />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Location">
            <HqInput
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Norway / EEA"
            />
          </Field>
          <Field label="Transfer mechanism">
            <HqInput
              value={transferMechanism}
              onChange={(e) => setTransferMechanism(e.target.value)}
              placeholder="EEA · GDPR"
            />
          </Field>
        </div>
        <Field label="DPA URL (optional)">
          <HqInput
            value={dpaUrl}
            onChange={(e) => setDpaUrl(e.target.value)}
            placeholder="https://…"
            mono
          />
        </Field>
        {err && (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: "var(--hq-radius-md)",
              background: "var(--hq-danger-soft)",
              color: "var(--hq-danger)",
              fontSize: 12,
              border: "1px solid rgba(251,113,133,.25)",
            }}
          >
            {err}
          </div>
        )}
      </div>
    </HqModal>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 11.5, color: "var(--hq-text-muted)", fontWeight: 500 }}>
        {label}
      </span>
      {children}
    </div>
  );
}
